// The sprays that can hold a block on a pick date — the read behind the
// harvest PHI chip (PhiHarvestSignal), shared with the morning offline prep
// (FM03 UF-02) so the prep seeds EXACTLY what the chip would fetch.
//
// One function for one block and for a whole farm: the chip asks for one id,
// the prep for every farm-owned block in a few chunked, drained reads instead
// of two requests per block (~160 on an 80-block farm — the storm FM11 UF-07
// exists to shrink). Same select, same filters, same merge, grouped by block.

import { supabase } from '@/lib/supabase'
import { fetchAllPagedIn } from '@/lib/inChunks'
import { fmtDayMonthZA, sastHM, sastYMD } from '@/lib/dates'
import { addDaysYmd, mergePhiSprayRows, type PhiSprayInput, type PhiSprayRow } from '@/lib/advisors/phiSafeToHarvest'

// Generously past the longest label PHI in the datasheet; sprays older than
// this cannot still be holding a block.
const PHI_LOOKBACK_DAYS = 180

/** The chip's cache key for a block on a pick date. The prep seeds this key. */
export function phiHarvestSpraysKey(blockId: string, pickDate: string) {
  return ['phi-harvest-sprays', blockId, addDaysYmd(pickDate, -PHI_LOOKBACK_DAYS), pickDate] as const
}

// The label PHIs ride along for the shared rule's fallback (FM10 F1): a spray
// with no PHI on its record is held by its longest label PHI.
const SPRAY_COLS =
  'spray_id, applied_at, pre_harvest_interval_days, product_name_override, project_id, chemical_id, ' +
  'chemicals:chemical_id ( product_name, pre_harvest_interval_days, spray_type, product_class ), ' +
  'spray_record_chemicals ( deleted_at, chemical_id, chemicals:chemical_id ( pre_harvest_interval_days, spray_type, product_class ) )'

type PrimaryRow = PhiSprayRow & { block_id: string }
type TargetRow = { block_id: string; project_id: string | null; spray_records: PhiSprayRow | null }

/** Each block's sprays in [from, to], merged per block (mergePhiSprayRows: the
 *  primary record wins a tie with its junction row; deleted parents drop).
 *  Every requested block gets an entry — an empty list is a real answer.
 *  Upper bound ≤ the pick date: a spray applied AFTER a backfilled pick date
 *  cannot hold that pick (L44). Throws on a failed read. */
export async function fetchPhiSpraysByBlock(
  blockIds: readonly string[], from: string, to: string, signal?: AbortSignal,
): Promise<Map<string, PhiSprayInput[]>> {
  const [primary, targets] = await Promise.all([
    fetchAllPagedIn<PrimaryRow, string>(blockIds, (chunk, lo, hi) => {
      const q = supabase.from('spray_records')
        .select(`block_id, ${SPRAY_COLS}`)
        .in('block_id', chunk)
        .is('deleted_at', null)
        .gte('applied_at', from)
        .lte('applied_at', to)
        .order('spray_id').range(lo, hi)
      return (signal ? q.abortSignal(signal) : q) as unknown as PromiseLike<{ data: PrimaryRow[] | null; error: { message: string } | null }>
    }),
    // Sprays that hit a block as a secondary target. The junction row's OWN
    // project_id is the spray's planting scope on THAT block (mig
    // 20261016222316), distinct from the parent record's. `!inner` puts the
    // date window on the server, so a farm-wide read does not drag back every
    // junction row ever written with a null parent.
    fetchAllPagedIn<TargetRow, string>(blockIds, (chunk, lo, hi) => {
      const q = supabase.from('spray_block_targets')
        .select(`block_id, spray_id, project_id, spray_records:spray_id!inner ( ${SPRAY_COLS}, deleted_at )`)
        .in('block_id', chunk)
        .is('deleted_at', null)
        .gte('spray_records.applied_at', from)
        .lte('spray_records.applied_at', to)
        .order('target_id').range(lo, hi)
      return (signal ? q.abortSignal(signal) : q) as unknown as PromiseLike<{ data: TargetRow[] | null; error: { message: string } | null }>
    }),
  ])
  if (primary.error) throw new Error(primary.error)
  if (targets.error) throw new Error(targets.error)
  return groupPhiSprays(blockIds, primary.rows, targets.rows)
}

/** Split the two reads by block, then merge each block on its own. */
function groupPhiSprays(
  blockIds: readonly string[], primary: PrimaryRow[], targets: TargetRow[],
): Map<string, PhiSprayInput[]> {
  const byBlock = new Map(blockIds.map(id => [id, { primary: [] as PhiSprayRow[], targets: [] as TargetRow[] }]))
  for (const r of primary) byBlock.get(r.block_id)?.primary.push(r)
  for (const t of targets) byBlock.get(t.block_id)?.targets.push(t)
  return new Map([...byBlock].map(([id, b]) => [id, mergePhiSprayRows(b.primary, b.targets)]))
}

/** The chip's own read for one block. */
export async function fetchBlockPhiSprays(blockId: string, from: string, to: string): Promise<PhiSprayInput[]> {
  return (await fetchPhiSpraysByBlock([blockId], from, to)).get(blockId) ?? []
}

/** FM03 UF-02: an answer served from this phone's cache (offline, or a
 *  refresh that failed) says when it was read, and what it cannot know. */
export function phiCheckedText(updatedAt: number, now: number = Date.now()): string {
  const day = sastYMD(updatedAt)
  const when = day === sastYMD(now)
    ? sastHM(updatedAt)
    : `${fmtDayMonthZA(day)} ${sastHM(updatedAt)}`
  return `Checked ${when} on this phone. Sprays recorded since then are not included.`
}
